import { useCallback, useState } from 'react';

import useToggle from 'shared/hooks/use-toggle';

import { toast } from 'react-hot-toast';

import { softwareApi } from '../api/systemRegisterApi';

import { softwareToRequest } from '../utils/softwareMappers';

import { apiErrorMessage, unwrapList, unwrapRecords } from '../utils/responseHelpers';
import { sortRowsNewestFirst } from 'shared/utils/sortRowsNewestFirst';



async function fetchAllSoftware(showAll = false) {
  const response = await softwareApi.list({ page: 0, size: 100, status: showAll ? 'ALL' : 'ACTIVE' });
  const { rows } = unwrapRecords(response);
  return rows;
}

/** One request: softwares + requested nested collection (or 1:1). */
async function fetchNestedSoftware(collection, showAll = false) {
  if (!collection) return [];
  const response = await softwareApi.listNested({
    collection,
    status: showAll ? 'ALL' : 'ACTIVE',
  });
  const data = response.data?.data;
  return Array.isArray(data) ? data : [];
}



function ensureNestedParent(request, collectionPath) {

  if (collectionPath.length === 1) {

    const key = collectionPath[0];

    if (!request[key]) {

      request[key] = [];

    }

    return request;

  }

  if (collectionPath[0] === 'dataInformation') {

    if (!request.dataInformation) {

      request.dataInformation = {

        personalData: false,

        dataSources: [],

        sensitiveDataCategories: [],

      };

    }

    if (!request.dataInformation.dataSources) {

      request.dataInformation.dataSources = [];

    }

  }

  return request;

}



function getCollectionList(request, collectionPath) {

  ensureNestedParent(request, collectionPath);

  if (collectionPath.length === 1) {

    return request[collectionPath[0]];

  }

  const [parentKey, childKey] = collectionPath;

  return request[parentKey][childKey];

}



function setCollectionList(request, collectionPath, list) {

  if (collectionPath.length === 1) {

    request[collectionPath[0]] = list;

    return request;

  }

  const [parentKey, childKey] = collectionPath;

  request[parentKey][childKey] = list;

  return request;

}



function applyCollectionMutation(request, config, row, values, isDelete) {

  const list = [...getCollectionList(request, config.collectionPath)];



  if (isDelete) {

    setCollectionList(

      request,

      config.collectionPath,

      list.filter((item) => item.id !== row.id)

    );

    return request;

  }



  const payload = config.toPayload(values);

  if (row?.id) {

    setCollectionList(

      request,

      config.collectionPath,

      list.map((item) => (item.id === row.id ? { ...item, ...payload, id: row.id } : item))

    );

  } else {

    setCollectionList(request, config.collectionPath, [...list, payload]);

  }

  return request;

}



export default function useEntityCrud(config) {

  const [items, setItems] = useState([]);

  const [totalRecords, setTotalRecords] = useState(0);

  const [isLoading, toggleLoading] = useToggle();

  const [softwareOptions, setSoftwareOptions] = useState([]);



  const loadSoftwareOptions = useCallback(async () => {

    try {

      const systems = await fetchAllSoftware();

      setSoftwareOptions(

        systems.map((s) => ({ id: s.id, label: s.softwareName || `Software #${s.id}` }))

      );

    } catch {

      setSoftwareOptions([]);

    }

  }, []);



  const fetchItems = useCallback(async (opts = {}) => {

    const showAll = Boolean(opts.showAll);

    toggleLoading();

    try {

      if (config.mode === 'lookup') {

        const response = await config.api.list({ status: showAll ? 'ALL' : 'ACTIVE' });

        const rows = sortRowsNewestFirst(unwrapList(response).map(config.toRow));

        setItems(rows);

        setTotalRecords(rows.length);

      } else if (config.mode === 'standalone') {
        const response = await config.api.list(
          config.listParams ? config.listParams(showAll) : undefined
        );
        const rows = sortRowsNewestFirst(
          unwrapList(response).map((item) =>
            config.toRow ? config.toRow(item) : item
          )
        );
        setItems(rows);
        setTotalRecords(rows.length);
      } else if (config.mode === 'nested' || config.mode === 'nestedCollection') {
        const collection =
          config.mode === 'nestedCollection'
            ? config.collectionPath?.[0]
            : config.nestedKey;
        const systems = await fetchNestedSoftware(collection, showAll);

        const statusBySoftwareId = new Map(
          systems.map((s) => [s.id, s.status || 'ACTIVE'])
        );

        const rows = sortRowsNewestFirst(
          config.flatten(systems).map((row) => ({
            ...row,
            softwareStatus: statusBySoftwareId.get(row.softwareId) || row.softwareStatus || 'ACTIVE',
          }))
        );

        setItems(rows);

        setTotalRecords(rows.length);

      }

    } catch (error) {

      toast.error(apiErrorMessage(error, `Failed to load ${config.entityName} records.`));

      setItems([]);

      setTotalRecords(0);

    } finally {

      toggleLoading();

    }

  }, [config, toggleLoading]);



  const createItem = useCallback(

    async (values) => {

      if (config.mode === 'lookup' || config.mode === 'standalone') {

        const response = await config.api.create(config.toPayload(values));

        return response.data?.data ?? response.data;

      }

      if (config.mode === 'nested') {

        const softwareId = values.softwareId;

        const detailRes = await softwareApi.get(softwareId);

        const detail = detailRes.data?.data;

        const request = config.applyNested(detail, values, false);

        const response = await softwareApi.update(softwareId, request);

        return response.data?.data ?? response.data;

      }

      if (config.mode === 'nestedCollection') {

        const softwareId = values.softwareId;

        const detailRes = await softwareApi.get(softwareId);

        const detail = detailRes.data?.data;

        let request = softwareToRequest(detail);

        request = applyCollectionMutation(request, config, null, values, false);

        const response = await softwareApi.update(softwareId, request);

        return response.data?.data ?? response.data;

      }

      return null;

    },

    [config]

  );



  const updateItem = useCallback(

    async (row, values) => {

      if (config.mode === 'lookup') {

        const response = await config.api.update(row.id, config.toPayload(values));

        return response.data?.data ?? response.data;

      }

      if (config.mode === 'standalone' && config.api?.update) {
        const payload = config.toPayload
          ? config.toPayload({
              ...values,
              targetType: values?.targetType
                || (row?.softwareId ? 'SOFTWARE' : 'IT_ASSET'),
              softwareId: values?.softwareId ?? row?.softwareId ?? null,
              assetRegisterIds: values?.assetRegisterIds ?? row?.assetRegisterIds ?? [],
            })
          : values;
        const response = await config.api.update(row.id, payload);
        return response.data?.data ?? response.data;
      }

      if (config.mode === 'standalone' && config.updateViaNested && row?.softwareId) {
        const softwareId = row.softwareId;
        const detailRes = await softwareApi.get(softwareId);
        const detail = detailRes.data?.data;
        let request = softwareToRequest(detail);
        request = applyCollectionMutation(
          request,
          {
            collectionPath: config.collectionPath || ['incidents'],
            toPayload: config.toNestedPayload || config.toPayload,
          },
          row,
          values,
          false
        );
        const response = await softwareApi.update(softwareId, request);
        return response.data?.data ?? response.data;
      }

      if (config.mode === 'nested') {

        const softwareId = row.softwareId;

        const detailRes = await softwareApi.get(softwareId);

        const detail = detailRes.data?.data;

        const request = config.applyNested(detail, values, false);

        const response = await softwareApi.update(softwareId, request);

        return response.data?.data ?? response.data;

      }

      if (config.mode === 'nestedCollection') {

        const softwareId = row.softwareId;

        const detailRes = await softwareApi.get(softwareId);

        const detail = detailRes.data?.data;

        let request = softwareToRequest(detail);

        request = applyCollectionMutation(request, config, row, values, false);

        const response = await softwareApi.update(softwareId, request);

        return response.data?.data ?? response.data;

      }

      return null;

    },

    [config]

  );



  const removeItem = useCallback(

    async (row) => {

      if (config.mode === 'lookup') {

        await config.api.remove(row.id);

        return;

      }

      if (config.mode === 'standalone' && config.api?.remove) {
        await config.api.remove(row.id);
        return;
      }

      if (config.mode === 'standalone' && config.updateViaNested && row?.softwareId) {
        const softwareId = row.softwareId;
        const detailRes = await softwareApi.get(softwareId);
        const detail = detailRes.data?.data;
        let request = softwareToRequest(detail);
        request = applyCollectionMutation(
          request,
          {
            collectionPath: config.collectionPath || ['incidents'],
            toPayload: config.toNestedPayload || config.toPayload,
          },
          row,
          {},
          true
        );
        await softwareApi.update(softwareId, request);
        return;
      }

      if (config.mode === 'nested') {

        const softwareId = row.softwareId;

        const detailRes = await softwareApi.get(softwareId);

        const detail = detailRes.data?.data;

        const request = config.applyNested(detail, {}, true);

        await softwareApi.update(softwareId, request);

        return;

      }

      if (config.mode === 'nestedCollection') {

        const softwareId = row.softwareId;

        const detailRes = await softwareApi.get(softwareId);

        const detail = detailRes.data?.data;

        let request = softwareToRequest(detail);

        request = applyCollectionMutation(request, config, row, {}, true);

        await softwareApi.update(softwareId, request);

      }

    },

    [config]

  );

  const restoreItem = useCallback(
    async (row) => {
      if (config.api?.restore) {
        await config.api.restore(row.id);
      }
    },
    [config]
  );

  return {

    items,

    totalRecords,

    isLoading,

    fetchItems,

    createItem,

    updateItem,

    removeItem,

    restoreItem,

    softwareOptions,

    loadSoftwareOptions,

  };

}



export { fetchAllSoftware };

